import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  LinearProgress,
  Typography
} from '@mui/material'
import { TrendingUp, CheckCircle } from '@mui/icons-material'

const GeneratingProgress = ({ t, phase1Progress, phase1Processed, phase1Total, phase2Progress, phase2Processed, phase2Total }) => {
  const phase1Complete = phase1Progress === 100
  const phase2Complete = phase2Progress === 100
  const currentPhase = phase1Complete ? 2 : 1

  return (
    <Box
      sx={{
        p: 3,
        backgroundColor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <TrendingUp sx={{ color: 'primary.main', mr: 1.5, fontSize: '1.25rem' }}/>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: 'primary.main'
          }}
        >
          {t('mailTemplate.campaigns.progress.generatingTitle')}
        </Typography>
      </Box>

      {/* Phase 1: Email Query */}
      <Box sx={{ mb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Box
            sx={{
              width: 24,
              height: 24,
              minWidth: 24,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: phase1Complete ? '#4caf50' : currentPhase === 1 ? '#1976d2' : '#e0e0e0',
              color: 'white',
              transition: 'all 0.3s ease',
              mt: 0.25
            }}
          >
            {phase1Complete
              ? <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'white' }}>✓</Typography>
              : <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'white' }}>1</Typography>
            }
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: currentPhase === 1 ? 600 : 500,
                  color: phase1Complete ? '#4caf50' : currentPhase === 1 ? 'text.primary' : 'text.secondary'
                }}
              >
                {t('mailTemplate.campaigns.progress.phase1Title')}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: phase1Complete ? '#4caf50' : 'text.primary',
                  ml: 2
                }}
              >
                {phase1Total === 0 ? t('mailTemplate.campaigns.progress.loading') : `${phase1Processed}/${phase1Total}`}
              </Typography>
            </Box>
            <LinearProgress
              variant={phase1Total === 0 && currentPhase === 1 ? 'indeterminate' : 'determinate'}
              value={phase1Progress}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: 'rgba(0, 0, 0, 0.08)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  background: phase1Complete
                    ? 'linear-gradient(90deg, #4caf50 0%, #66bb6a 100%)'
                    : 'linear-gradient(90deg, #7c4dff 0%, #b388ff 100%)'
                }
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Phase 2: Email Content Generation */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Box
            sx={{
              width: 24,
              height: 24,
              minWidth: 24,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: phase2Complete ? '#4caf50' : currentPhase === 2 ? '#1976d2' : '#e0e0e0',
              color: 'white',
              transition: 'all 0.3s ease',
              mt: 0.25
            }}
          >
            {phase2Complete
              ? <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'white' }}>✓</Typography>
              : <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'white' }}>2</Typography>
            }
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: currentPhase === 2 ? 600 : 500,
                  color: phase2Complete ? '#4caf50' : currentPhase === 2 ? 'text.primary' : 'text.secondary'
                }}
              >
                {t('mailTemplate.campaigns.progress.phase2Title')}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: phase2Complete ? '#4caf50' : currentPhase === 2 ? 'text.primary' : 'text.secondary',
                  ml: 2
                }}
              >
                {currentPhase === 1 ? '-' : phase2Total === 0 ? t('mailTemplate.campaigns.progress.loading') : `${phase2Processed}/${phase2Total}`}
              </Typography>
            </Box>
            <LinearProgress
              variant={currentPhase === 2 && phase2Total === 0 ? 'indeterminate' : 'determinate'}
              value={currentPhase === 1 ? 0 : phase2Progress}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: 'rgba(0, 0, 0, 0.08)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  background: phase2Complete
                    ? 'linear-gradient(90deg, #4caf50 0%, #66bb6a 100%)'
                    : 'linear-gradient(90deg, #2196f3 0%, #64b5f6 100%)'
                }
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

const CampaignProgressBar = ({ campaign, firms = [], totalContactCount = 0, emailReadyContactCount = 0 }) => {
  const { t } = useTranslation()

  const progressData = useMemo(() => {
    if (!campaign) {
      return { progress: 0, totalCount: 0, processedCount: 0, shouldShow: false, isIndeterminate: false }
    }

    const status = campaign.status

    if (status === 'READY_TO_SELECT') {
      const total = firms.length || 1

      return {
        progress: 100,
        processedCount: total,
        totalCount: total,
        shouldShow: true,
        statusType: 'preparing',
        isIndeterminate: false
      }
    }

    if (status === 'CONTACTS_SEARCHING') {
      const selectedFirms = firms.filter(firm => firm.selected === true)
      const total = selectedFirms.length

      const processed = selectedFirms.filter(firm =>
        firm.contacts && firm.contacts.length > 0
      ).length

      const hasData = total > 0

      return {
        progress: hasData ? (processed / total) * 100 : 0,
        processedCount: processed,
        totalCount: total,
        shouldShow: true,
        statusType: 'searchingContacts',
        isIndeterminate: !hasData
      }
    }

    if (status === 'CONTACTS_FOUND') {
      return {
        progress: 100,
        processedCount: 0,
        totalCount: 0,
        shouldShow: false,
        statusType: 'searchingContacts',
        isIndeterminate: false
      }
    }

    if (status === 'READY_TO_MAIL') {
      const selectedFirms = firms.filter(firm => firm.selected === true)
      const total = selectedFirms.length || 1

      return {
        progress: 100,
        processedCount: total,
        totalCount: total,
        shouldShow: true,
        statusType: 'generating',
        isIndeterminate: false
      }
    }

    if (!firms.length) {
      if (status === 'PREPARING' || status === 'GENERATING') {
        return {
          progress: 0,
          totalCount: 0,
          processedCount: 0,
          shouldShow: true,
          statusType: status === 'PREPARING' ? 'preparing' : 'generating',
          isIndeterminate: false
        }
      }

      return { progress: 0, processedCount: 0, totalCount: 0, shouldShow: false, isIndeterminate: false }
    }

    if (status === 'PREPARING') {
      const total = firms.length
      const processed = firms.filter(firm =>
        firm.explanation &&
        firm.isRelevant !== null && firm.isRelevant !== undefined
      ).length

      return {
        progress: total > 0 ? (processed / total) * 100 : 0,
        processedCount: processed,
        totalCount: total,
        shouldShow: true,
        statusType: 'preparing',
        isIndeterminate: false
      }
    }

    if (status === 'GENERATING') {
      const phase1Total = totalContactCount
      const phase1Processed = emailReadyContactCount
      const phase1Progress = phase1Total > 0 ? (phase1Processed / phase1Total) * 100 : 0
      const phase1Complete = phase1Progress === 100

      let phase2Total = 0
      let phase2Processed = 0

      firms.filter(firm => firm.selected === true).forEach(firm => {
        const contacts = firm.contacts || []

        contacts.filter(contact => contact.selected === true).forEach(contact => {
          phase2Total++
          if (contact.emailContent !== null && contact.emailContent !== undefined) {
            phase2Processed++
          }
        })
      })

      const phase2Progress = phase2Total > 0 ? (phase2Processed / phase2Total) * 100 : 0

      return {
        shouldShow: true,
        statusType: 'generating',
        isGenerating: true,
        phase1Progress,
        phase1Processed,
        phase1Total,
        phase1Complete,
        phase2Progress,
        phase2Processed,
        phase2Total
      }
    }

    return { progress: 0, processedCount: 0, totalCount: 0, shouldShow: false, isIndeterminate: false }
  }, [campaign, firms, totalContactCount, emailReadyContactCount])

  if (!progressData.shouldShow) {
    return null
  }

  if (progressData.isGenerating) {
    return (
      <GeneratingProgress
        t={t}
        phase1Progress={progressData.phase1Progress}
        phase1Processed={progressData.phase1Processed}
        phase1Total={progressData.phase1Total}
        phase2Progress={progressData.phase2Progress}
        phase2Processed={progressData.phase2Processed}
        phase2Total={progressData.phase2Total}
      />
    )
  }

  const { progress, totalCount, processedCount, statusType, isIndeterminate } = progressData
  const isComplete = progress === 100

  return (
    <Box
      sx={{
        p: 3,
        backgroundColor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        {isComplete
          ? (
            <CheckCircle sx={{ color: 'success.main', mr: 1.5, fontSize: '1.25rem' }}/>
            )
          : (
            <TrendingUp sx={{ color: 'primary.main', mr: 1.5, fontSize: '1.25rem' }}/>
            )}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: isComplete ? 'success.main' : 'primary.main'
          }}
        >
          {statusType === 'preparing'
            ? t('mailTemplate.campaigns.progress.preparingTitle')
            : statusType === 'searchingContacts'
              ? t('mailTemplate.campaigns.progress.searchingContactsTitle')
              : t('mailTemplate.campaigns.progress.generatingTitle')
          }
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {statusType === 'preparing'
            ? t('mailTemplate.campaigns.progress.preparingDescription')
            : statusType === 'searchingContacts'
              ? t('mailTemplate.campaigns.progress.searchingContactsDescription')
              : t('mailTemplate.campaigns.progress.generatingDescription')
          }
        </Typography>
        {!isIndeterminate && (
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: isComplete ? 'success.main' : 'text.primary'
            }}
          >
            {totalCount === 0 ? t('mailTemplate.campaigns.progress.loading') : `${processedCount}/${totalCount} (${Math.floor(progress)}%)`}
          </Typography>
        )}
      </Box>

      <LinearProgress
        variant={isIndeterminate ? 'indeterminate' : 'determinate'}
        value={isIndeterminate ? undefined : progress}
        sx={{
          height: 6,
          borderRadius: 3,
          backgroundColor: 'rgba(0, 0, 0, 0.08)',
          '& .MuiLinearProgress-bar': {
            borderRadius: 3,
            background: isComplete
              ? 'linear-gradient(90deg, #4caf50 0%, #66bb6a 100%)'
              : statusType === 'preparing'
                ? 'linear-gradient(90deg, #ff9800 0%, #ffb74d 100%)'
                : statusType === 'searchingContacts'
                  ? 'linear-gradient(90deg, #26a69a 0%, #4db6ac 100%)'
                  : 'linear-gradient(90deg, #2196f3 0%, #64b5f6 100%)'
          }
        }}
      />

    </Box>
  )
}

export default CampaignProgressBar
